import type { Dispatch, ReducersMapObject, Store } from "redux";
import { type TypedUseSelectorHook, useSelector, useDispatch } from "react-redux";
import modalReducer from "../../common-deprecated/redux/reducers/ModalReducer";
import utilReducer from "../../common-deprecated/redux/reducers/UtilReducer";
import type { CreateLogicType } from "../../common-deprecated/redux/typeHelpers";
import CommonSettingsReducer from "../../common-deprecated/redux/reducers/CommonSettingsReducer";
import { getInitStoreByReducers } from "../../common-deprecated/redux/storeHelpers";
import TradeInAemSettingsReducer, { type TradeInAemSettingsType } from "./reducers/TradeInAemSettingsReducer";
import type { TradeInAemSettingsActionsType } from "./actions/TradeInAemSettingsActions";
import TradeInAemNavReducer, { type TradeInAemNavReducerType } from "./reducers/TradeInAemNavReducer";
import type { TradeInAemNavActionsType } from "./actions/TradeInAemNavActions";
import TradeInAemModelSelectReducer, {
    type TradeInAemModelSelectReducerType,
} from "./reducers/TradeInAemModelSelectReducer";
import type { TradeInAemModelSelectActions } from "./actions/TradeInAemModelSelectActions";
import TradeInAemVehicleReducer, { type TradeInAemVehicleReducerType } from "./reducers/TradeInAemVehicleReducer";
import TradeInAemFindVehicleReducer, {
    type TradeInAemFindVehicleReducerType,
} from "./reducers/TradeInAemFindVehicleReducer";
import type { TradeInAemVehicleActionsType } from "./actions/TradeInAemVehicleActions";
import type { TradeInAemFindVehicleActionsType } from "./actions/TradeInAemFindVehicleActions";
import TradeInAemDealerReducer, { type TradeInAemDealerReducerType } from "./reducers/TradeInAemDealerReducer";
import type { TradeInAemDealerActionsType } from "./actions/TradeInAemDealerActions";
import {
    type TradeInAemVehicleDetailsReducerType,
    TradeInAemVehicleDetailsReducer,
} from "./reducers/TradeInAemVehicleDetailsReducer";
import type { TradeInAemVehicleDetailsActions } from "./actions/TradeInAemVehicleDetailsActions";
import type { TradeInAemVehicleValueActions } from "./actions/TradeInAemVehicleValueActions";
import TradeInAemVehicleValueReducer, {
    type TradeInAemVehicleValueReducerType,
} from "./reducers/TradeInAemVehicleValueReducer";
import TradeInAemContactFormReducer, {
    type TradeInAemContactFormReducerType,
} from "./reducers/TradeInAemContactFormReducer";
import type { TradeInAemContactFormActions } from "./actions/TradeInAemContactFormActions";
import findVehicleLogic from "./logic/findVehicleLogic";
import { type TradeInAemUtilsReducerType, TradeInAemUtilsReducer } from "./reducers/TradeInAemUtilsReducer";
import type { TradeInAemUtilsActions } from "./actions/TradeInAemUtilsActions";
import TradeInAemExternalDataReducer, {
    type TradeInAemExternalDataReducerType,
} from "../../common-deprecated/features/tradein/redux/reducers/TradeInAemExternalDataReducer";
import type { TradeInAemExternalDataActions } from "../../common-deprecated/features/tradein/redux/actions/TradeInAemExternalDataActions";
import type { CommonActionsType, CommonReducerType } from "../../common-deprecated/redux/commonStore";

export type TradeInAemStateType = CommonReducerType & {
    tradeInAemNav: TradeInAemNavReducerType;
    tradeInAemVehicle: TradeInAemVehicleReducerType;
    tradeInAemFindVehicle: TradeInAemFindVehicleReducerType;
    tradeInAemVehicleDetails: TradeInAemVehicleDetailsReducerType;
    tradeInAemVehicleValue: TradeInAemVehicleValueReducerType;
    tradeInAemModelSelect: TradeInAemModelSelectReducerType;
    tradeInAemDealer: TradeInAemDealerReducerType;
    tradeInAemContact: TradeInAemContactFormReducerType;
    tradeInAemUtils: TradeInAemUtilsReducerType;
    tradeInAemSettings: TradeInAemSettingsType;
    tradeInAemExternalData: TradeInAemExternalDataReducerType;
};

export type TradeInAemActionsType =
    | CommonActionsType
    | TradeInAemNavActionsType
    | TradeInAemVehicleActionsType
    | TradeInAemFindVehicleActionsType
    | TradeInAemVehicleDetailsActions
    | TradeInAemVehicleValueActions
    | TradeInAemModelSelectActions
    | TradeInAemDealerActionsType
    | TradeInAemContactFormActions
    | TradeInAemUtilsActions
    | TradeInAemExternalDataActions
    | TradeInAemSettingsActionsType;

export type TradeInAemDispatchType = Dispatch<TradeInAemActionsType>;

export type TradeInAemLogicType<ActionsType extends TradeInAemActionsType> = CreateLogicType<
    ActionsType,
    TradeInAemStateType,
    TradeInAemDispatchType
>;

export type TradeInAemStoreType = Store<TradeInAemStateType, TradeInAemActionsType>;

export const useTradeInAemDispatch = useDispatch<TradeInAemDispatchType>;

export const useTradeInAemSelector: TypedUseSelectorHook<TradeInAemStateType> = useSelector;

export const tradeInAemReducers: ReducersMapObject<TradeInAemStateType, any> = {
    tradeInAemNav: TradeInAemNavReducer,
    tradeInAemVehicle: TradeInAemVehicleReducer,
    tradeInAemFindVehicle: TradeInAemFindVehicleReducer,
    tradeInAemVehicleDetails: TradeInAemVehicleDetailsReducer,
    tradeInAemVehicleValue: TradeInAemVehicleValueReducer,
    tradeInAemModelSelect: TradeInAemModelSelectReducer,
    tradeInAemDealer: TradeInAemDealerReducer,
    tradeInAemContact: TradeInAemContactFormReducer,
    tradeInAemUtils: TradeInAemUtilsReducer,
    tradeInAemSettings: TradeInAemSettingsReducer,
    tradeInAemExternalData: TradeInAemExternalDataReducer,
    commonSettings: CommonSettingsReducer,
    modal: modalReducer,
    utils: utilReducer,
};

export const tradeInAemClientLogic = [...findVehicleLogic];

export const tradeInAemServerStore = getInitStoreByReducers(tradeInAemReducers, []);
export const tradeInAemClientStore = getInitStoreByReducers(tradeInAemReducers, tradeInAemClientLogic);
